import { useCallback, useMemo, useState } from 'react';
import Sheet, {
    CellContentContainer,
    Change as SheetChange,
    SheetRenderProps,
    Style as CellStyle,
} from 'sheet-happens';
import { useI18nContext } from '@i18n/i18n-react';

import useTheme from '@base/useTheme';
import {
    BatchUpdateImportJobRowsRow,
    ColumnMeta,
    ColumnMetaDataContext,
    ColumnMetaDateFormat,
    EntityType,
    ImportJobRepresentation,
    ImportJobRowsResponse,
    ImportJobStatus,
} from '@common/api';
import { Button } from '@components/button';
import { useModal } from '@components/modal/ModalProvider';
import { editImage, filterImage, sheetIconProps } from '@components/sheet/sheetIcons';
import { PageLayout, PageSheet } from '@components/page/PageLayout';
import { useImportJobRouteParams } from '@data/hooks/importJobsRouteParams';
import { useImportJobColumnFilters, useImportJobFormattedRows } from '@data/hooks/importJobData';
import {
    useBatchUpdateImportJobRows,
    useDetectDimensions,
    useImportJobRows,
    useImportJobs,
    useUpdateImportJob,
} from '@data/importJob/importJobActions';
import { useGlobalLoadingContext } from '@features/loading/globalLoadingContext';
import useTableCells from '@hooks/useTableCells';
import { useFormatting } from '@providers/FormattingProvider';
import { PAGE_ICONS } from '@util/folderItems';

import { ImportJobActions } from './ImportJobActions';
import ColumnEditModal from './ColumnEditModal';
import { columnMetaEqual, convertDateToImportFormat, parseDateWithImportFormat } from './util';
import type { ColumnFilter, ImportJobRowData, ImportJobRowInfo } from './types';
import { useEntityContext } from '@providers/EntityProvider';
import { Labels } from '@features/labels/Labels';
import { useLabelActions } from './useImportJobLabelAction';

const DEFAULT_COLUMN_WIDTH = 170;
const columnMetaDataContextDescriptionMap: Partial<Record<ColumnMetaDataContext, string>> = {
    [ColumnMetaDataContext.IDENTIFIER]: 'Table',
    [ColumnMetaDataContext.TIMEPOINT]: 'Value for month',
    [ColumnMetaDataContext.VALUES]: 'Value',
    [ColumnMetaDataContext.DATETIME]: 'Date',
    [ColumnMetaDataContext.DESCRIPTION]: 'Description',
    [ColumnMetaDataContext.CUSTOM]: 'Custom field',
};

const NO_JOBS: ImportJobRepresentation[] = [];
const NO_ROWS: ImportJobRowsResponse = { rows: [], isTruncated: false };

function createHeaders(
    columnMetas: ColumnMeta[],
    columnFilters: readonly ColumnFilter[],
    openColumnEditModal: (columnIndex: number) => void
) {
    /* add btns to first row */
    return (columnIndex: number) => {
        const columnMeta = columnMetas[columnIndex];
        const columnFilter = columnFilters[columnIndex];
        if (!columnMeta || !columnFilter) return null;

        const hasFilter =
            (columnFilter.hasEmptyCells && !columnFilter.showEmptyCells) ||
            columnFilter.values.some((v) => !columnFilter.showValues.get(v.raw));

        const headerContent: CellContentContainer = {
            items: [],
        };
        const typeDescription = columnMetaDataContextDescriptionMap[columnMeta.dataContext];
        const headerTitle = typeDescription !== undefined ? `${columnMeta.name} (${typeDescription})` : columnMeta.name;
        headerContent.items.push({
            display: 'inline',
            text: headerTitle,
            textAlign: 'left',
        });

        headerContent.items.push({
            ...sheetIconProps,
            image: hasFilter ? filterImage : editImage,
            onClick: () => openColumnEditModal(columnIndex),
        });
        return headerContent;
    };
}

interface ImportJobSheetProps {
    importJob: Readonly<ImportJobRepresentation>;
}

type UserChanges = Partial<Record<number, Partial<Record<number, ImportJobRowData>>>>;

function ImportJobSheet({ importJob }: ImportJobSheetProps) {
    // general hooks
    const { LL } = useI18nContext();
    const { parseDate, parseNumber, formatDate, formatNumber } = useFormatting();
    const theme = useTheme();
    const { showModal } = useModal();
    const { globalLoadingWrapped } = useGlobalLoadingContext();
    const { getEntity } = useEntityContext();

    // data hooks
    const importJobRowsResponse = useImportJobRows(importJob.id, { notifyOnChangeProps: ['data'] }).data ?? NO_ROWS;
    const importJobRows = importJobRowsResponse.rows;
    const isRowsTruncated = importJobRowsResponse.isTruncated;
    const batchUpdateImportJobRows = useBatchUpdateImportJobRows(importJob.id);
    const updateImportJob = useUpdateImportJob();
    const detectDimensions = useDetectDimensions();

    // columns, filters, rows
    const columnMetas = importJob.metadata.columnMetas;
    const [columnFilters, setColumnFilter] = useImportJobColumnFilters(importJobRows, columnMetas);
    const formattedRows = useImportJobFormattedRows(importJobRows, columnMetas, columnFilters);

    // states
    const [userChanges, setUserChanges] = useState<UserChanges>({});
    const { cellWidth, cellHeight, onCellWidthChange, onCellHeightChange } = useTableCells(
        new Array(columnMetas.length).fill(DEFAULT_COLUMN_WIDTH)
    );
    const importJobEditable =
        importJob.status === ImportJobStatus.CREATED || importJob.status === ImportJobStatus.REVERTED;

    const openColumnEditModal = useCallback(
        (columnIndex: number) => {
            const columnMeta = columnMetas[columnIndex] as Readonly<ColumnMeta> | undefined;
            if (columnMeta === undefined) {
                return;
            }
            const columnFilter = columnFilters[columnIndex] as Readonly<ColumnFilter> | undefined;
            if (columnFilter === undefined) {
                return;
            }
            const updateColumnMeta = async (newColumnMeta: Readonly<ColumnMeta>) => {
                const oldColumnMeta = columnMetas[columnIndex];
                if (columnMetaEqual(oldColumnMeta, newColumnMeta)) {
                    // metadata hasn't changed, only reference has
                    // don't update anything
                    return;
                }
                // column meta has changed, update it
                const columnMetasCopy = [...columnMetas];
                columnMetasCopy[columnIndex] = newColumnMeta;
                const metadataCopy = { ...importJob.metadata, columnMetas: columnMetasCopy };
                await updateImportJob.mutateAsync({ id: importJob.id, metadata: metadataCopy });
                if (newColumnMeta.dataContext === ColumnMetaDataContext.IDENTIFIER) {
                    // if we set (or kept) the column to table column, check dimensions
                    await detectDimensions.mutateAsync(importJob.id);
                }
            };
            const setColumnFilterModal = (newColumnFilter: Readonly<ColumnFilter>) =>
                setColumnFilter(columnIndex, newColumnFilter);
            showModal(
                <ColumnEditModal
                    columnMeta={columnMeta}
                    columnFilter={columnFilter}
                    updateColumnMeta={updateColumnMeta}
                    setColumnFilter={setColumnFilterModal}
                    importJobEditable={importJobEditable}
                />
            );
        },
        [
            columnMetas,
            columnFilters,
            setColumnFilter,
            importJob.metadata,
            importJob.id,
            updateImportJob,
            detectDimensions,
            showModal,
            importJobEditable,
        ]
    );
    const tableHeaders = useMemo(
        () => createHeaders(columnMetas, columnFilters, openColumnEditModal),
        [columnMetas, columnFilters, openColumnEditModal]
    );

    const headerStyle = (columnIndex: number) => {
        const columnHeaderStyle: CellStyle = {
            fontFamily: 'Inter',
            fontSize: 12,
            color: theme.colors.ui.dark,
        };

        const column = columnMetas[columnIndex];
        if (
            column?.dataContext === ColumnMetaDataContext.VALUES ||
            column?.dataContext === ColumnMetaDataContext.TIMEPOINT
        ) {
            columnHeaderStyle.color = theme.colors.secondary.w500;
        } else if (column?.dataContext === ColumnMetaDataContext.IDENTIFIER) {
            columnHeaderStyle.color = theme.colors.primary.w500;
        }

        if (!column?.dataContext || column.dataContext === ColumnMetaDataContext.NONE)
            columnHeaderStyle.color = theme.colors.neutral.w700;

        return columnHeaderStyle;
    };

    const cellStyle = (columnIndex: number, rowIndex: number) => {
        const cellStyle: CellStyle = {
            fontFamily: 'Inter',
            fontSize: 12,
            color: theme.colors.ui.dark,
        };

        const thisRowInfo = formattedRows[rowIndex] as Readonly<ImportJobRowInfo> | undefined;
        if (thisRowInfo === undefined) {
            return cellStyle;
        }

        const thisCellStyleData = thisRowInfo.style?.[columnIndex];
        if (thisCellStyleData === 'tag') {
            // color detected tags
            cellStyle.color = theme.colors.primary.w500;
        } else if (thisCellStyleData === 'number') {
            // align numbers
            cellStyle.textAlign = 'right';
        }

        const column = columnMetas[columnIndex];
        if (
            (column?.dataContext === ColumnMetaDataContext.VALUES ||
                column?.dataContext === ColumnMetaDataContext.TIMEPOINT) &&
            column?.applyTagsIds?.[0] &&
            getEntity({ id: column.applyTagsIds[0], type: EntityType.Variable })
        ) {
            cellStyle.color = theme.colors.secondary.w500;
        }

        if (!column?.dataContext || column.dataContext === ColumnMetaDataContext.NONE)
            cellStyle.color = theme.colors.neutral.w700;

        // check for user changes
        const thisCellChange = userChanges[thisRowInfo.rowId]?.[columnIndex];
        if (thisCellChange !== undefined) {
            // color user changes
            cellStyle.color = theme.colors.warning.w500;
            if (thisCellChange === null) {
                cellStyle.backgroundColor = theme.colors.warning.w50;
            }
        }

        // Rows that won't be committed (auto-skipped by the per-job rules or
        // manually toggled off) render muted so the user can see at a glance
        // which rows are excluded. We use `fillColor` (painted in a dedicated
        // earlier pass) rather than `backgroundColor` (painted inside
        // renderCell), because backgroundColor would (a) be skipped entirely
        // for empty cells so an all-null skipped row wouldn't get tinted,
        // and (b) paint over the selection outline and the cell gridlines.
        if (thisRowInfo.skipped) {
            cellStyle.color = theme.colors.neutral.w600;
            cellStyle.fillColor = theme.colors.neutral.w200;
            cellStyle.fontWeight = '300';
        }

        return cellStyle;
    };

    const readOnly = (columnIndex: number, rowIndex: number) => {
        if (!importJobEditable) {
            return true;
        }

        if (rowIndex >= formattedRows.length) {
            // no existing row
            return true;
        }

        if (columnIndex >= columnMetas.length) {
            // no existing column
            return true;
        }

        // data can be edited
        return false;
    };

    const onChange = (sheetChanges: SheetChange[]) =>
        setUserChanges((previousUserChanges) => {
            const userChangesCopy = { ...previousUserChanges };
            for (const sheetChange of sheetChanges) {
                const originalRow = formattedRows[sheetChange.y] as Readonly<ImportJobRowInfo> | undefined;
                if (!originalRow) {
                    // don't save any changes if row doesn't exist
                    continue;
                }
                const columnMeta = columnMetas[sheetChange.x];
                if (!columnMeta) {
                    // don't save any changes if column doesn't exist
                    continue;
                }

                // copy previous changes or initialize an empty object
                const rowChangesCopy = { ...userChangesCopy[originalRow.rowId] };
                const rawValue = sheetChange.value;
                let newValue: ImportJobRowData;

                if (rawValue === null) {
                    newValue = null;
                } else if (
                    columnMeta.dataContext === ColumnMetaDataContext.VALUES ||
                    columnMeta.dataContext === ColumnMetaDataContext.TIMEPOINT
                ) {
                    // column has numbers -> try to parse value
                    const parsedValue =
                        typeof rawValue === 'number' ? rawValue : parseNumber(parseFloat(rawValue.replace(',', '.')));
                    newValue = Number.isNaN(parsedValue) ? null : parsedValue ?? null;
                } else if (columnMeta.dataContext === ColumnMetaDataContext.DATETIME) {
                    // column has dates -> try to parse it
                    const parsedDate =
                        typeof rawValue === 'string'
                            ? parseDate(rawValue) // given value is a string -> parse it with locale format
                            : parseDateWithImportFormat(rawValue, ColumnMetaDateFormat.EXCEL); // given value is a number -> parse it as excel serial date
                    newValue =
                        parsedDate === null ? null : convertDateToImportFormat(parsedDate, columnMeta.dateFormat);
                } else {
                    newValue = rawValue;
                }

                const originalValue = originalRow.originalRowData[sheetChange.x];
                const valuesAreEqual =
                    newValue === originalValue ||
                    (newValue === null && (originalValue === '' || originalValue === null));

                if (valuesAreEqual) {
                    delete rowChangesCopy[sheetChange.x];
                } else {
                    rowChangesCopy[sheetChange.x] = newValue;
                }

                if (Object.keys(rowChangesCopy).length === 0) {
                    delete userChangesCopy[originalRow.rowId];
                } else {
                    userChangesCopy[originalRow.rowId] = rowChangesCopy;
                }
            }
            return userChangesCopy;
        });

    const displayData = (columnIndex: number, rowIndex: number): ImportJobRowData => {
        // fetch data
        const importJobRow = formattedRows[rowIndex] as Readonly<ImportJobRowInfo> | undefined;
        if (importJobRow === undefined) {
            // empty cell if no import job row exists on this index
            return null;
        }

        // show changed values if changes were made
        const rowChanges = userChanges[importJobRow.rowId];
        if (rowChanges !== undefined) {
            const change = rowChanges[columnIndex] as ImportJobRowData | undefined;
            if (change !== undefined) {
                const thisColumnMeta = columnMetas[columnIndex];
                if (change !== null && thisColumnMeta.dataContext === ColumnMetaDataContext.DATETIME) {
                    // format dates for display (tableDate will have already formatted ones)
                    // change value must remain same to properly update raw value in row
                    const parsedDate = parseDateWithImportFormat(change, thisColumnMeta.dateFormat);
                    if (parsedDate === null) {
                        return null;
                    } else {
                        const formattedDate = formatDate(parsedDate);
                        return formattedDate;
                    }
                } else if (
                    typeof change === 'number' &&
                    (thisColumnMeta.dataContext === ColumnMetaDataContext.VALUES ||
                        thisColumnMeta.dataContext === ColumnMetaDataContext.TIMEPOINT)
                ) {
                    return formatNumber(change);
                } else {
                    return change;
                }
            }
        }

        // return original value
        const data = importJobRow.formattedRowData[columnIndex];
        if (importJobRow.style[columnIndex] === 'number') {
            return formatOriginalRowDataNumber(data);
        }
        return data;
    };

    // puts limit on decimal places amount because of numfmt package internal limits
    function formatOriginalRowDataNumber(data: ImportJobRowData) {
        const isNumeric =
            typeof data === 'number' || (typeof data === 'string' && !isNaN(parseFloat(data)) && isFinite(+data));

        if (isNumeric) {
            const num = typeof data === 'number' ? data : parseFloat(data);
            const integerPartLength = Math.floor(Math.abs(num)).toString().length;
            const decimalPlaces = Math.max(0, 10 - integerPartLength);

            return formatNumber(parseFloat(num.toFixed(decimalPlaces))) as ImportJobRowData;
        }

        return data;
    }

    const displayEditData = (columnIndex: number, rowIndex: number): ImportJobRowData => {
        const importJobRow = formattedRows[rowIndex] as Readonly<ImportJobRowInfo> | undefined;
        if (!importJobRow) {
            return null;
        }

        if (importJobRow.style[columnIndex] !== 'number') {
            return displayData(columnIndex, rowIndex);
        }

        const rowChanges = userChanges[importJobRow.rowId];
        const change = rowChanges?.[columnIndex] as ImportJobRowData | undefined;

        return change !== undefined ? change : importJobRow.originalRowData[columnIndex];
    };

    const sourceData = (x: number, y: number): ImportJobRowData => {
        return displayData(x, y);
    };

    const editData = (x: number, y: number): string => {
        const data = displayEditData(x, y);
        if (data === null) {
            return '';
        } else {
            return String(data);
        }
    };

    const totalSkippedCount = useMemo(() => importJobRows.filter((r) => r.skipped).length, [importJobRows]);

    // Per-row manual override for the ⊘/✓ glyph in the row-number gutter.
    // Writes directly to import_job_row.skipped via batchUpdateImportJobRows.
    const toggleSkipForRows = useCallback(
        async (rowIds: number[], skip: boolean) => {
            if (rowIds.length === 0) return;
            await batchUpdateImportJobRows.mutateAsync(rowIds.map((id) => ({ id, skipped: skip })));
        },
        [batchUpdateImportJobRows]
    );

    const renderRowOverlays = useCallback(
        ({ visibleCells, cellLayout }: SheetRenderProps) => {
            const ICON_WIDTH = 22;
            return (
                <>
                    {visibleCells.rows.map((y: number) => {
                        const row = formattedRows[y] as Readonly<ImportJobRowInfo> | undefined;
                        if (!row) return null;
                        const top = cellLayout.rowToPixel(y);
                        const height = cellLayout.rowToPixel(y, 1) - top;
                        const nextSkipped = !row.skipped;
                        return (
                            <div
                                key={`overlay-${y}`}
                                style={{
                                    position: 'absolute',
                                    left: 2,
                                    top: top + 2,
                                    width: ICON_WIDTH,
                                    height: Math.max(height - 4, 16),
                                    pointerEvents: importJobEditable ? 'auto' : 'none',
                                    cursor: importJobEditable ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 13,
                                    lineHeight: 1,
                                    color: row.skipped ? theme.colors.neutral.w600 : theme.colors.primary.w500,
                                    backgroundColor: 'transparent',
                                    borderRadius: 4,
                                    transition: 'background-color 80ms ease-out',
                                    userSelect: 'none',
                                }}
                                onMouseEnter={(e) => {
                                    if (!importJobEditable) return;
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor =
                                        theme.colors.neutral.w200;
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (importJobEditable) toggleSkipForRows([row.rowId], nextSkipped);
                                }}
                                // Sheet-happens listens to mousedown for selection and to
                                // dblclick to start cell editing. Without these handlers a
                                // fast double-click on the glyph bubbles up and puts A1 into
                                // edit mode.
                                onMouseDown={(e) => e.stopPropagation()}
                                onDoubleClick={(e) => e.stopPropagation()}
                                title={row.skipped ? LL.INCLUDE_IN_IMPORT() : LL.EXCLUDE_FROM_IMPORT()}
                            >
                                {row.skipped ? '⊘' : '✓'}
                            </div>
                        );
                    })}
                </>
            );
        },
        [formattedRows, importJobEditable, theme.colors, toggleSkipForRows]
    );

    const saveChanges = async () => {
        await globalLoadingWrapped(async () => {
            // map update data to format for api
            const mappedUpdateData: BatchUpdateImportJobRowsRow[] = [];
            for (const [rowIdString, rowChanges] of Object.entries(userChanges)) {
                const rowId = Number.parseInt(rowIdString);
                if (rowChanges === undefined || Number.isNaN(rowId)) {
                    continue;
                }
                const originalRow = formattedRows.find((rowInfo) => rowInfo.rowId === rowId);
                if (originalRow === undefined) {
                    continue;
                }
                const updatedRowData = [...originalRow.originalRowData];
                for (const [indexString, newValue] of Object.entries(rowChanges)) {
                    const index = Number.parseInt(indexString);
                    if (newValue === undefined || Number.isNaN(index)) {
                        continue;
                    }
                    updatedRowData[index] = newValue;
                }
                mappedUpdateData.push({ id: rowId, data: updatedRowData });
            }
            // call api with update data
            await batchUpdateImportJobRows.mutateAsync(mappedUpdateData);
            // reset frontend changes
            setUserChanges({});
        });
    };

    const undoChanges = () => {
        setUserChanges({});
    };

    return (
        <>
            {isRowsTruncated && (
                <div
                    style={{
                        padding: '12px 16px',
                        marginBottom: 8,
                        backgroundColor: theme.colors.warning.w50,
                        color: theme.colors.warning.w700,
                        borderRadius: 4,
                        fontSize: 14,
                    }}
                >
                    {LL.IMPORT_ROWS_TRUNCATED_WARNING()}
                </div>
            )}
            {importJobEditable && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '8px 16px',
                        fontSize: 12,
                        color: theme.colors.neutral.w700,
                    }}
                >
                    <span>
                        {totalSkippedCount > 0
                            ? LL.ROWS_WILL_BE_SKIPPED_ON_IMPORT({ count: totalSkippedCount })
                            : LL.NO_ROWS_WILL_BE_SKIPPED_ON_IMPORT()}
                    </span>
                </div>
            )}
            <PageSheet>
                <Sheet
                    columnHeaders={tableHeaders}
                    columnHeaderStyle={headerStyle}
                    editData={editData}
                    displayData={displayData}
                    sourceData={sourceData}
                    cellStyle={cellStyle}
                    cellWidth={cellWidth}
                    onCellWidthChange={onCellWidthChange}
                    cellHeight={cellHeight}
                    onCellHeightChange={onCellHeightChange}
                    onChange={onChange}
                    renderInside={renderRowOverlays}
                    readOnly={readOnly}
                />
            </PageSheet>
            {Object.keys(userChanges).length > 0 && (
                <div
                    style={{
                        position: 'absolute',
                        right: 50,
                        bottom: 50,
                        width: 140,
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Button title="Save" onClick={saveChanges} />
                    <Button title="Undo" $type="secondary" onClick={undoChanges} />
                </div>
            )}
        </>
    );
}

export default function ImportJobSingle() {
    const { LL } = useI18nContext();
    const { importJobId } = useImportJobRouteParams();
    const importJobsQuery = useImportJobs();
    const importJobs = importJobsQuery.data ?? NO_JOBS;
    const selectedImportJob = importJobs.find((ij) => ij.id === importJobId);

    const { addLabel, removeLabel, renameLabel, createLabel, deleteLabel } = useLabelActions(
        selectedImportJob ?? null,
        importJobs
    );

    const allLabels = useMemo(() => {
        const labels = new Set<string>();
        importJobs.forEach((job) => job.labels?.forEach((l) => labels.add(l)));
        return Array.from(labels);
    }, [importJobs]);

    if (selectedImportJob !== undefined) {
        // found the selected import job
        const title = selectedImportJob.title;
        const actions = <ImportJobActions importJob={selectedImportJob} />;
        const labelsAction = (
            <Labels
                labels={selectedImportJob.labels}
                getLabelName={(label: string) => label}
                onAdd={addLabel}
                onRemove={removeLabel}
                onUpdate={renameLabel}
                allLabels={allLabels}
                onCreate={createLabel}
                onDelete={deleteLabel}
            />
        );
        return (
            <PageLayout
                title={title}
                actions={
                    <>
                        {labelsAction}
                        {actions}
                    </>
                }
                icon={PAGE_ICONS.Import}
                padded
            >
                <ImportJobSheet importJob={selectedImportJob} />
            </PageLayout>
        );
    }

    // query loading - show nothing (will refresh once loaded)
    if (importJobsQuery.isLoading || importJobsQuery.isFetching || importJobsQuery.isRefetching) {
        const title = LL.LOADING_IMPORT_JOBS();
        return <PageLayout title={title} />;
    }

    // selected import job data not found
    const title = LL.SELECTED_JOB_DOESNT_EXIST();
    return (
        <PageLayout title={title} padded>
            <div>
                <Button
                    title={LL.GO_BACK_TO_IMPORT()}
                    onClick={() => {
                        window.location.hash = '/import';
                    }}
                />
            </div>
        </PageLayout>
    );
}
